// CreditOverageWarning — warns the user, *before* they hit send, that
// their next chat turn will push their monthly credit usage over the
// cap (or already has). Lives just above the composer so the warning
// is impossible to miss without taking up space when everything is
// fine — the component returns `null` for unaffected users.
//
// Estimation strategy
// -------------------
//   1. Token estimate: prior turns + current draft → input tokens.
//   2. Add an assumed assistant reply length (`ASSUMED_OUTPUT_TOKENS`
//      inside `tokenEstimate`).
//   3. Convert to points using the locked margin/fee formula.
//   4. Compare projected total (`used + projectedPoints`) to the plan's
//      monthly limit. We don't have a points→credits mapping per plan,
//      so we treat 1 point ≈ 1 credit for the warning threshold — this
//      is a UX nudge, not a hard meter, and the credit hook already
//      drives the authoritative gauge underneath.
import { useMemo } from "react";
import { AlertTriangle, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCreditLimit } from "@/hooks/useCreditLimit";
import {
  estimateConversationTokens,
  pointsForUsage,
  formatPoints,
  type ChatLike,
} from "@/lib/tokenEstimate";
import { cn } from "@/lib/utils";

interface Props {
  /** Prior chat turns (we only use role + content for the estimate). */
  turns: ChatLike[];
  /** Current composer draft — drives the live recompute. */
  composer: string;
  /** Model the next send will use (drives pricing). */
  model: string;
}

const ASSUMED_OUTPUT = 600; // matches tokenEstimate's ASSUMED_OUTPUT_TOKENS

const CreditOverageWarning = ({ turns, composer, model }: Props) => {
  const { user } = useAuth();
  const { data: credit } = useCreditLimit();

  const projection = useMemo(() => {
    if (!credit || credit.limit === null) return null;
    const inputTokens = estimateConversationTokens(turns, composer);
    const projectedPoints = pointsForUsage(model, {
      prompt_tokens: inputTokens,
      completion_tokens: ASSUMED_OUTPUT,
    });
    const remaining = Math.max(0, credit.limit - credit.used);
    const willExceed = projectedPoints > remaining;
    const alreadyOver = credit.used >= credit.limit;
    return { projectedPoints, remaining, willExceed, alreadyOver };
  }, [credit, turns, composer, model]);

  // Quiet for: signed-out, unlimited plans, no draft yet, comfortably under cap.
  if (!user || !credit || credit.limit === null) return null;
  if (!projection) return null;
  if (!projection.willExceed && !projection.alreadyOver) return null;

  const { projectedPoints, remaining, alreadyOver } = projection;

  return (
    <div
      role="alert"
      className={cn(
        "mb-2 rounded-md border px-3 py-2 text-xs flex items-start gap-2",
        alreadyOver
          ? "border-destructive/40 bg-destructive/10 text-destructive"
          : "border-amber-400/40 bg-amber-400/10 text-amber-600 dark:text-amber-400",
      )}
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="font-medium">
          {alreadyOver
            ? "You're over your monthly credit limit."
            : "Heads up — this message will push you over your monthly limit."}
        </p>
        <p className="opacity-80 font-mono text-[10px]">
          {alreadyOver ? (
            <>
              Used {credit.used.toLocaleString()} / {credit.limit.toLocaleString()} credits this
              month. Upgrade to keep sending.
            </>
          ) : (
            <>
              Estimated cost: ~{formatPoints(projectedPoints)} pts ·{" "}
              {remaining.toLocaleString()} credits left
            </>
          )}
        </p>
      </div>
      {credit.nextTier && (
        <Link
          to="/pricing"
          className={cn(
            "shrink-0 inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-mono whitespace-nowrap transition-colors",
            alreadyOver
              ? "border-destructive/40 hover:bg-destructive/20"
              : "border-amber-400/40 hover:bg-amber-400/20",
          )}
        >
          <Crown className="w-3 h-3" />
          Upgrade
        </Link>
      )}
    </div>
  );
};

export default CreditOverageWarning;